import { useEffect, useMemo, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import { useLocalStorage } from 'react-use';

import type { MarketingConsent } from '../stores/marketing-consent/MarketingConsentStore.types';
import { useStores } from '../stores/useStores';

type Props = {
  initialMarketingConsents: MarketingConsent[];
  fetchingMarketingConsents: boolean;
  localStorageKey: string;
};
export const useMarketingConsents = ({
  initialMarketingConsents,
  fetchingMarketingConsents,
  localStorageKey,
}: Props) => {
  const { marketingConsentStore } = useStores();
  const { marketingConsentAnswers } = marketingConsentStore;

  const [marketingConsentsLS, setMarketingConsentsLS, removeMarketingConsentsLS] = useLocalStorage<
    Record<string, boolean>
  >(localStorageKey, {});

  const [initializedMarketingConsents, setInitializedMarketingConsents] = useState(false);

  useEffect(() => {
    if (fetchingMarketingConsents) {
      return;
    }

    if (!isEmpty(marketingConsentsLS)) {
      const mappedAnswers = Object.entries(marketingConsentsLS).reduce(
        (acc: Record<string, boolean>, [consentId, checked]) => {
          acc[consentId] = checked;
          return acc;
        },
        {},
      );

      marketingConsentStore.setMarketingConsentAnswers(mappedAnswers);
    } else if (initialMarketingConsents.length > 0) {
      const map = initialMarketingConsents.reduce((acc: Record<string, boolean>, consent) => {
        acc[consent.id] = false;
        return acc;
      }, {});
      marketingConsentStore.setMarketingConsentAnswers(map);
    } else {
      marketingConsentStore.setMarketingConsentAnswers({});
    }

    setInitializedMarketingConsents(true);
  }, [initialMarketingConsents, fetchingMarketingConsents, marketingConsentsLS, marketingConsentStore]);

  const onChangeMarketingConsent = (checked: boolean, consentId: string) => {
    marketingConsentStore.setMarketingConsentAnswers({ [consentId]: checked });
    setMarketingConsentsLS({ ...marketingConsentsLS, [consentId]: checked });
  };

  // If all optional consents are not checked, move to RSG else continue as normal flow
  const areAllOptionalConsentsChecked = useMemo(
    () =>
      Object.values(initialMarketingConsents)
        .filter((consent) => consent.isApprovalRequired === false)
        .every((consent) => marketingConsentAnswers[consent.id] === true),
    [initialMarketingConsents, marketingConsentAnswers],
  );

  const areAllMandatoryConsentsChecked = useMemo(
    () =>
      Object.values(initialMarketingConsents)
        .filter((consent) => consent.isApprovalRequired === true)
        .every((consent) => marketingConsentAnswers[consent.id] === true),
    [initialMarketingConsents, marketingConsentAnswers],
  );

  const marketingConsentsAnswers = useMemo(() => {
    const set = new Set<string>(initialMarketingConsents.map((consent) => consent.id));
    return Object.entries(marketingConsentAnswers).reduce((acc: Record<string, boolean>, [consentId, checked]) => {
      if (set.has(consentId)) {
        acc[consentId] = checked;
      }
      return acc;
    }, {});
  }, [marketingConsentAnswers, initialMarketingConsents]);

  return {
    marketingConsents: marketingConsentsAnswers,
    initializedMarketingConsents,
    onChangeMarketingConsent,
    setMarketingConsentsLS,
    removeMarketingConsentsLS,
    areAllOptionalConsentsChecked,
    areAllMandatoryConsentsChecked,
  };
};
